import { BaseInput, ErrorMessage, Text } from '@shared/widgets';
import { Controller, UseFormReturn } from 'react-hook-form';
import { Select } from '@shared/widgets/select';
import { $countryProvincesStore, $countryStore, $recipientMandatoryFieldsStore, $transferCurrenciesStore } from '@lib/store';
import { useUnit } from 'effector-react';
import { TransferRecipientAccountTypeEnum } from '@shared/api/rest/recipients';
import { IbanUtils, ValidateUtils } from '@shared/utils';
import { CountryResponse } from '@shared/api/rest/misc';
import { IFormDataBusinessCreate } from '../../../payments/subPages/recipientEditor/subPages/businessRecipient';
import { IFormDataBusinessBetween, IFormDataIndividualBetween } from '../betweenAccount';
import { IFormDataBusinessOther, IFormDataIndividualOther } from '../otherTransfer';
import { IFormDataIndividualCreate } from '../../../payments/subPages/recipientEditor/subPages/individualRecipient';
import { useEffect, useMemo } from 'react';

export type AccountFormDataType =
  | IFormDataBusinessCreate
  | IFormDataBusinessBetween
  | IFormDataBusinessOther
  | IFormDataIndividualBetween
  | IFormDataIndividualCreate
  | IFormDataIndividualOther;

interface ICountryOptions {
  label: string;
  value: string;
}

interface PropsType {
  formControl: UseFormReturn<AccountFormDataType, undefined>;
  countryOptions: ICountryOptions[];
  countryStore: CountryResponse[];
}

const ACCOUNT_TYPE_OPTIONS = Object.entries(TransferRecipientAccountTypeEnum).map((x) => {
  return { label: x[0], value: x[1] };
});

export const AccountFormBlock = (props: PropsType) => {
  const mandatoryFields = useUnit($recipientMandatoryFieldsStore.store);
  const countryStore = useUnit($countryStore.store);
  const transferCurrencies = useUnit($transferCurrenciesStore.store);
  const countryProvincesStore = useUnit($countryProvincesStore.store);

  const iban = props.formControl.watch('iban');
  const accountNumber = props.formControl.watch('accountNumber');

  const accountNumberRequired = !iban;
  const ibanRequired = !accountNumber;

  const bankCountry = props.formControl.watch('bankCountry');

  const selectedBankProvinces = bankCountry ? (bankCountry in countryProvincesStore.data ? countryProvincesStore.data[bankCountry] : []) : [];

  const countryOptions = useMemo(() => {
    return countryStore.data.map((x) => {
      return { label: x.name, value: x.twoLetterCode };
    });
  }, [countryStore]);

  useEffect(() => {
    if (iban) {
      props.formControl.trigger('accountNumber');
    }
  }, [iban]);

  useEffect(() => {
    if (accountNumber) {
      props.formControl.trigger('iban');
    }
  }, [accountNumber]);

  useEffect(() => {
    const validateIban = async () => {
      if (!iban) return;

      const ibanValidation = await IbanUtils.validateIban(iban);
      const { control, setValue, clearErrors, setError } = props.formControl;

      if (ibanValidation.valid) {
        const countryCode = IbanUtils.getCountry(iban);
        const selectedCountry = countryStore.data.find((e) => e.twoLetterCode === countryCode);

        if (selectedCountry?.twoLetterCode !== control._formValues.bankCountry) {
          setValue('bankCountry', selectedCountry?.twoLetterCode, { shouldValidate: true });
        }

        if (ibanValidation.bankData.bic !== undefined) {
          setValue('bic', ibanValidation.bankData.bic, { shouldValidate: true });
        }

        if (ibanValidation.bankData.name) {
          setValue('bankName', ibanValidation.bankData.name, { shouldValidate: true });
        }

        clearErrors('iban');
      } else {
        setError('iban', { type: 'manual', message: 'Provided invalid account IBAN number' });
      }
    };

    validateIban();
  }, [iban]);

  return (
    <>
      <Text className='mt-4'>Account Details</Text>
      <div className='w-full my-6 flex gap-3'>
        <Controller
          name='bankCountry'
          control={props.formControl.control}
          rules={{ required: ValidateUtils.requiredErrorMessage }}
          render={({ field: { value, onChange }, fieldState: { error, isDirty } }) => {
            if (isDirty && error) {
              props.formControl.clearErrors('bankCountry');
            }
            return (
              <div className='flex-col w-1/2'>
                <Select
                  className='border border-light'
                  wrapperClassName='bg-white overflow-hidden'
                  buttonClassName='bg-transparent -rotate-90'
                  iconClassName='!text-primary dark:!text-white'
                  items={countryOptions}
                  nullValue={{ isEnable: true, text: 'Bank Country' }}
                  value={value}
                  required
                  enableSearch
                  onChange={(e) => {
                    onChange(e);
                    props.formControl.setValue('purpose', '');
                  }}
                />

                <ErrorMessage text={error?.message} />
              </div>
            );
          }}
        />
        <Controller
          name='currency'
          control={props.formControl.control}
          rules={{
            required: accountNumberRequired ? ValidateUtils.requiredErrorMessage : undefined,
            maxLength: 100,
          }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <div className='flex-col w-1/2'>
              <Select
                className='border border-light'
                wrapperClassName='bg-white overflow-hidden'
                buttonClassName='bg-transparent -rotate-90'
                iconClassName='!text-primary dark:!text-white'
                items={transferCurrencies.data}
                value={value}
                onChange={onChange}
              />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
      </div>
      <Controller
        name='iban'
        control={props.formControl.control}
        rules={{
          required: ibanRequired ? ValidateUtils.requiredErrorMessage : undefined,
          maxLength: 100,
        }}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <>
            <BaseInput className='my-2' placeholder='IBAN number' required={ibanRequired} value={value} onChange={onChange} />
            <ErrorMessage text={error?.message} />
          </>
        )}
      />
      <Controller
        name='bankName'
        control={props.formControl.control}
        rules={{
          required: ValidateUtils.requiredErrorMessage,
          maxLength: 100,
          validate: (value) => {
            return ValidateUtils.validateFields(value, 'Bank Name');
          },
        }}
        render={({ field: { value, onChange }, fieldState: { error } }) => {
          return (
            <>
              <BaseInput className='my-2' placeholder='Bank Name' required value={value} onChange={onChange} />
              <ErrorMessage text={error?.message} />
            </>
          );
        }}
      />
      <Controller
        name='accountNumber'
        control={props.formControl.control}
        rules={{
          required: accountNumberRequired ? ValidateUtils.requiredErrorMessage : undefined,
          maxLength: 100,
          validate: (value) => {
            return value ? ValidateUtils.validateFields(value, 'Account Number') : undefined;
          },
        }}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <>
            <BaseInput className='my-2' placeholder='Account Number' required={accountNumberRequired} value={value} onChange={onChange} />
            <ErrorMessage text={error?.message} />
          </>
        )}
      />
      <Controller
        name='accountType'
        control={props.formControl.control}
        rules={{
          required: ValidateUtils.requiredErrorMessage,
          maxLength: 100,
        }}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <>
            <Select
              wrapperClassName='overflow-hidden'
              buttonClassName='bg-transparent '
              iconClassName='!text-primary dark:!text-white'
              items={ACCOUNT_TYPE_OPTIONS}
              nullValue={{ isEnable: true, text: 'Account type' }}
              value={value}
              required
              onChange={onChange}
            />
            <ErrorMessage text={error?.message} />
          </>
        )}
      />
      <Controller
        name='bic'
        control={props.formControl.control}
        rules={{
          required: ValidateUtils.requiredErrorMessage,
          maxLength: 100,
          validate: (value) => {
            const isValid = ValidateUtils.validateBicCodeField(value);
            return isValid ? undefined : ValidateUtils.bicSwiftErrorMessage;
          },
        }}
        render={({ field: { value, onChange }, fieldState: { error } }) => {
          return (
            <>
              <BaseInput className='my-2' placeholder='BIC/Swift code' required value={value} onChange={onChange} />
              <ErrorMessage text={error?.message} />
            </>
          );
        }}
      />
      <Controller
        control={props.formControl.control}
        name='bankProvince'
        rules={{
          maxLength: 100,
          ...(mandatoryFields.data.includes('insi_bankprovince')
            ? {
                required: ValidateUtils.requiredErrorMessage,
              }
            : { required: undefined }),
        }}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <>
            <Select
              className='mt-2'
              wrapperClassName='overflow-hidden '
              buttonClassName='bg-transparent'
              iconClassName='!text-primary dark:!text-white'
              items={selectedBankProvinces}
              nullValue={{ isEnable: true, text: 'Bank Province / Region' }}
              required={mandatoryFields.data.includes('insi_bankprovince') ? true : false}
              value={value || null}
              enableSearch
              onChange={onChange}
            />
            <ErrorMessage text={error?.message} />
          </>
        )}
      />
      <Controller
        control={props.formControl.control}
        name='bankAddress'
        rules={{
          maxLength: 100,
          ...(mandatoryFields.data.includes('insi_bankaddress')
            ? {
                required: ValidateUtils.requiredErrorMessage,
              }
            : { required: undefined }),
          validate: (value) => {
            return ValidateUtils.validateFields(value, 'Bank Address');
          },
        }}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <>
            <BaseInput
              className='my-2'
              placeholder='Bank Address'
              required={mandatoryFields.data.includes('insi_bankaddress') ? true : false}
              value={value}
              onChange={onChange}
            />
            <ErrorMessage text={error?.message} />
          </>
        )}
      />
      <Controller
        control={props.formControl.control}
        name='bankCity'
        rules={{
          maxLength: 100,
          ...(mandatoryFields.data.includes('insi_bankcity')
            ? {
                required: ValidateUtils.requiredErrorMessage,
              }
            : { required: undefined }),
          validate: (value) => {
            return ValidateUtils.validateFields(value, 'Bank City');
          },
        }}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <>
            <BaseInput
              className='my-2'
              placeholder='Bank City'
              required={mandatoryFields.data.includes('insi_bankcity') ? true : false}
              value={value}
              onChange={onChange}
            />
            <ErrorMessage text={error?.message} />
          </>
        )}
      />
      <Controller
        control={props.formControl.control}
        name='branchCode'
        rules={{
          maxLength: 100,
          ...(mandatoryFields.data.includes('insi_branchcode')
            ? {
                required: ValidateUtils.requiredErrorMessage,
              }
            : { required: undefined }),
          validate: (value) => {
            return ValidateUtils.validateFields(value, 'Branch Code');
          },
        }}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <>
            <BaseInput
              className='my-2'
              placeholder='Branch Code / Sort Code / Routing Code'
              required={mandatoryFields.data.includes('insi_branchcode') ? true : false}
              value={value}
              onChange={onChange}
            />
            <ErrorMessage text={error?.message} />
          </>
        )}
      />
    </>
  );
};
